import { useEffect, useMemo, useState } from 'react';

import styles from './unified-login-carousel.module.css';

const buyerSlides = [
  {
    image:
      'https://images.unsplash.com/photo-1633158829585-23ba8f7c8caf?q=80&w=2000&auto=format&fit=crop',
    title: 'Find your place in the world.',
    highlight: 'place',
    subtitle: 'Clear steps, real guidance, and a path that feels like home from day one.',
  },
  {
    image:
      'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2000&auto=format&fit=crop',
    title: 'Build a future that lasts.',
    highlight: 'future',
    subtitle: 'Plan with confidence using tools that keep you informed and in control.',
  },
  {
    image:
      'https://images.unsplash.com/photo-1563986768609-322da13575f3?q=80&w=2000&auto=format&fit=crop',
    title: 'Unlock the home of your dreams.',
    highlight: 'home',
    subtitle: 'From first look to final offer, we simplify the journey without compromise.',
  },
];

const specialistSlides = [
  {
    image:
      'https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=2000&auto=format&fit=crop',
    title: 'Powered by Mira AI.',
    highlight: 'Mira AI',
    subtitle: 'Automate the busywork and focus on high-impact borrower relationships.',
  },
  {
    image:
      'https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=2000&auto=format&fit=crop',
    title: 'Close deals faster.',
    highlight: 'faster',
    subtitle: 'Stay ahead with instant insights, clean pipelines, and fast turnarounds.',
  },
  {
    image:
      'https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=2000&auto=format&fit=crop',
    title: 'Intelligence at scale.',
    highlight: 'Intelligence',
    subtitle: 'Operational clarity and AI-powered workflows built for growing teams.',
  },
];

const UnifiedLoginCarousel = ({ userType }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [progressSeed, setProgressSeed] = useState(0);

  const activeSlides = useMemo(
    () => (userType === 'buyer' ? buyerSlides : specialistSlides),
    [userType]
  );

  useEffect(() => {
    setCurrentSlide(0);
    setProgressSeed((seed) => seed + 1);
  }, [userType]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % activeSlides.length);
      setProgressSeed((seed) => seed + 1);
    }, 6000);

    return () => clearTimeout(timer);
  }, [currentSlide, activeSlides.length, userType]);

  const highlightClass = userType === 'buyer' ? styles.highlightBuyer : styles.highlightSpecialist;

  const renderTitle = (slide) => {
    const parts = slide.title.split(slide.highlight);
    if (parts.length === 1) {
      return slide.title;
    }

    return (
      <>
        {parts[0]}
        <span className={highlightClass}>{slide.highlight}</span>
        {parts.slice(1).join(slide.highlight)}
      </>
    );
  };

  return (
    <div className={styles.carouselPane}>
      {activeSlides.map((slide, index) => (
        <div
          key={slide.image}
          className={`${styles.slide} ${index === currentSlide ? styles.slideActive : styles.slideInactive}`}
        >
          <img src={slide.image} alt={slide.title} className={styles.slideImage} />
          <div className={styles.slideOverlay} />
          <div className={styles.carouselBlur} />
          <div className={styles.slideText}>
            <h2 className={styles.slideTitle}>{renderTitle(slide)}</h2>
            <p className={styles.slideSubtitle}>{slide.subtitle}</p>
          </div>
        </div>
      ))}

      <div className={styles.dots}>
        {activeSlides.map((slide, index) => {
          const isActive = index === currentSlide;

          return (
            <button
              key={`${slide.highlight}-${index}`}
              type="button"
              className={`${styles.dotButton} ${isActive ? styles.dotActive : styles.dotInactive}`}
              onClick={() => {
                setCurrentSlide(index);
                setProgressSeed((seed) => seed + 1);
              }}
              aria-label={`Go to slide ${index + 1}`}
            >
              {isActive && (
                <span
                  key={`progress-${index}-${progressSeed}`}
                  className={styles.dotProgress}
                  style={{ animationDuration: '6000ms' }}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default UnifiedLoginCarousel;
